import React, { useContext, useEffect, useState } from "react";
import "./HomePage.scss";
import {
  homeBackground,
  IconBridge,
  IconExperience,
  IconIndustry,
  IconRelationship,
} from "../../assets/assetReference";
import { useInView } from "react-intersection-observer";
import { motion } from "framer-motion";
import { NavLink } from "react-router-dom";
import { ContactEnquiry } from "../../components/Contact/Contact";
import SlideUpAnimation from "../../components/SlideUpAnimation/SlideUpAnimation";
import { Helmet } from "react-helmet-async";
import { events, insights, weekly } from "../../data/insight/insightsExport";
import Insight from "../../data/insight/insightClass";
import GrayBanner from "components/GrayBanner/GrayBanner";
import ServiceBox from "components/ServiceBox/ServiceBox";
import { useSelector } from "react-redux";
import { getIsMobile } from "features/app/UIRelatedSlice";
import RedLine from "components/Deco/RedLine";

export default function HomePage() {
  return (
    <>
      <Helmet>
        <title>Wind Energy Bridge</title>
        <meta
          name="description"
          content="WeBridge is a consulting firm bridging European offshore wind developers and international suppliers, providing expert services for the renewable energy industry. Learn more about our wind energy consulting services."
        />
        <meta
          name="keywords"
          content="WeBridge, wind energy bridge, offshore wind energy, offshore wind developers, offshore wind suppliers"
        />
      </Helmet>
      <div className={"home-page-container mb-[200px] h-full"}>
        <div className="relative w-screen h-screen -mt-[50px]">
          <img
            className="w-full h-full object-cover"
            src={homeBackground}
            alt="wind turbine"
          />
          {/* <div className="absolute inset-0 bg-white opacity-50"></div> */}
          <div className="absolute left-0 top-0 h-full w-full">
            <div className="h-full w-full px-4 mobile:px-6 sm:px-10 mx-auto flex flex-col items-start justify-center">
              <p className="text-white font-body-condensed text-lg mb-2">
                OFFSHORE WIND ENERGY
              </p>
              <h1 className="text-white text-shadow-lg font-bold text-6xl font-body-condensed leading-[1.2]">
                WORKING TOGETHER
                <br />
                FOR A SUSTAINABLE FUTURE
              </h1>
              <button
                type="button"
                className="border-2 border-white px-10 py-3 pb-3 mt-6 self-start"
              >
                <a
                  href="https://www.gwsce.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-main text-base text-start font-bold font-body-condensed text-white"
                >
                  <p className="font-normal">Monday, 16 June 2025</p>
                  <p className="text-lg">Exclusive London Networking Event</p>
                </a>
                {/* <NavLink
                to="/about-us"
                className="text-xs font-bold font-body-condensed text-white"
              >
                LEARN MORE
              </NavLink> */}
              </button>
            </div>
          </div>
        </div>
        {/* <ImgRedBannerTemplate
          backgroundImg={{
            src: windmillVideosArray[0],
            alt: "a video of many offshore windmill turbine generators, video zooms out",
            type: "video",
          }}
          // backgroundImg={{ src: windmillPhotosArray[2], alt: "offshore windmill photo" }}
          h1={["Working Together", "for a Sustainable Future"]}
          redBannerContent={[
            "We are WeBridge",
            "Connecting European Offshore Wind Projects with International Supply Chains.",
          ]}
          redBannerLink="/about-us"
          OtherContent={<OtherContent />}
          num={3}
          // delay={1.5}
          duration={2}
        /> */}
        <WhoWeAre />
        <div className="content flex flex-col gap-40">
          <WhatWeOffer />
          <WhyWeBridge />
          <Insights />
          <ContactUs />
        </div>
      </div>
    </>
  );
}

function WhoWeAre() {
  return (
    <GrayBanner
      tag="WHO WE ARE"
      title={
        <p>
          Wind Energy
          <br />
          Bridge
        </p>
      }
      button={{ color: "main", text: "CONTACT US" }}
      content={
        <>
          <p className="mb-4">
            Connecting European Offshore Wind Projects with International Supply
            Chains.
          </p>
          <p>
            We are WeBridge. WeBridge stands as an independent and influential
            advisor, bridging the gap between European offshore wind developers
            and global suppliers and service providers.
          </p>
        </>
      }
      padding={true}
    />
  );
}

function WhatWeOffer() {
  const services = [
    {
      title: "For Developers",
      description:
        "By offering complementary specialist services to European offshore wind developers, we effectively bridge the demand and supply gaps in Europe, facilitating successful collaborations and contributing to the growth and development of the European offshore wind market, and a sustainable future.",
      link: "/services/for-developers",
    },
    {
      title: "For Suppliers",
      description:
        "We offer comprehensive business development services tailored to international suppliers' specific needs and objectives, ensuring a successful market entry and sustainable growth in the European offshore wind sector.",
      link: "/services/for-suppliers",
    },
    {
      title: "For Investor",
      description:
        "WeBridge offers a comprehensive range of services to debt and equity investors in the offshore wind industry, designed to support their investing and lending decisions, risk assessment, and due diligence processes. ",
      link: "/services/for-investors",
    },
    {
      title: "For Insurers",
      description:
        "By leveraging our industry expertise, market insights, and network of key industry stakeholders, WeBridge bring a range of valuable services which empower insurers to navigate the offshore wind insurance landscape with confidence.",
      link: "/services/for-insurers",
    },
  ];

  return (
    <div className="block">
      <SlideUpAnimation triggerOnce={false}>
        <p className="tag">WHAT WE OFFER</p>
        <h2 className="subheading">Our Services</h2>
        <RedLine />
        <div className="grid-style mt-20">
          {services.map((i) => {
            return (
              <div className="">
                <h3 className="font-headings  text-2xl mb-10">{i.title}</h3>
                <div>
                  <p>{i.description}</p>
                  <NavLink
                    to={i.link}
                    className="inline-flex items-center mt-6 bg-main-gray/70 px-4 py-2 rounded-lg text-gray-400 hover:bg-main-gray"
                  >
                    <span>Learn More</span>{" "}
                    <span className="material-symbols-outlined opacity-30">
                      chevron_right
                    </span>
                  </NavLink>
                </div>
              </div>
            );
          })}
        </div>
      </SlideUpAnimation>
    </div>
  );
}

function WhyWeBridge() {
  //   {
  //     title: "Market Supply-Demand Analysis",
  //     icon: IconAnalysis,
  //     content: [
  //         "Provide valuable insights to European developers, enabling them to make informed decisions, identify potential opportunities for collaboration with international suppliers, and maximize the value of their offshore wind projects.",
  //         "Highlight the competitive advantages and unique offerings of international suppliers that can meet the market demand effectively.",
  //         "Suggest strategies for developers to optimise their supply chains, mitigate risks, and ensure timely project delivery.",
  //     ]
  // },
  const companySellingPoint = [
    {
      icon: IconExperience,
      title: "Extensive Experience",
      content: [
        "A strong team with successful track record in European offshore wind market",
      ],
    },
    {
      icon: IconIndustry,
      title: "Deep Industry Knowledge",
      content: [
        "A thorough understanding of European offshore wind market and developer’s requirements",
      ],
    },
    {
      icon: IconRelationship,
      title: "Strong Network",
      content: [
        "Established connections with key industry stakeholders, including developers and EPCI contractors",
      ],
    },
    {
      icon: IconBridge,
      title: "Cultural Bridge",
      content: [
        "Effective communication and collaboration between International and European counterparts",
      ],
    },
  ];
  return (
    <div>
      <p className="tag">HOW ARE WE DIFFERENT</p>
      <h2 className="subheading">Why WeBridge</h2>
      <div className="grid-style mt-20">
        {companySellingPoint.map((i, index) => {
          return (
            <SlideUpAnimation
              triggerOnce={true}
              transition={{ delay: index * 1 }}
            >
              <ServiceBox
                img={{ src: i.icon, alt: `icon for ${i.title}` }}
                item={i}
              />
              {/* <div className="">
                <i.icon width={40} height={40} />
                <div>
                  <h3>{i.title}</h3>
                  <p>{i.desc}</p>
                </div>
              </div> */}
            </SlideUpAnimation>
          );
        })}
      </div>
    </div>
  );
}

function Insights() {
  const [activeTab, setActiveTab] = useState("articles");
  const tabs = ["Articles", "Weekly", "Events"];
  return (
    <div className="w-full">
      <h2 className="subheading">WeBridge Insights</h2>
      <div className="flex space-x-4 mb-6 pt-4 border-b border-b-gray-600">
        {tabs.map((i) => {
          return (
            <button
              onClick={() => setActiveTab(i.toLowerCase())}
              className={`px-4 pb-2 font-bold text-sm ${
                activeTab === i.toLowerCase()
                  ? "text-main border-b-2  border-main"
                  : "text-gray-800"
              }`}
              type="button"
            >
              {i}
            </button>
          );
        })}
      </div>
      <div className="">
        {activeTab === "articles" && (
          <div className="insight-block-container">
            <div>
              {insights.map((i, index) => {
                if (index > 4) return <></>;
                const modifiedInsight = new Insight(
                  i.date,
                  i.seo,
                  i.img,
                  i.title,
                  i.sub,
                  i.preview,
                  i.jsx
                );
                modifiedInsight.heading = "h3";
                return modifiedInsight.smallPreviewJSX;
              })}
            </div>
            <button type="button">
              <NavLink to="/insights/articles">Learn More</NavLink>
            </button>
          </div>
        )}
        {activeTab === "weekly" && (
          <div className="insight-block-container">
            <div>
              {weekly.map((i, index) => {
                if (index > 4) return <></>;
                const modifiedInsight = new Insight(
                  i.date,
                  i.seo,
                  i.img,
                  i.title,
                  i.sub,
                  i.preview,
                  i.jsx
                );
                modifiedInsight.heading = "h3";
                return modifiedInsight.smallPreviewJSX;
              })}
            </div>
            <button type="button">
              <NavLink to="/insights/weekly">Learn More</NavLink>
            </button>
          </div>
        )}
        {activeTab === "events" && (
          <div className="insight-block-container">
            <div>
              {events.map((i, index) => {
                if (index > 4) return <></>;
                const modifiedInsight = new Insight(
                  i.date,
                  i.seo,
                  i.img,
                  i.title,
                  i.sub,
                  i.preview,
                  i.jsx
                );
                modifiedInsight.heading = "h3";
                return modifiedInsight.smallPreviewJSX;
              })}
            </div>
            <button type="button">
              <NavLink to="/insights/events">Learn More</NavLink>
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

function ContactUs() {
  return (
    <div className="block contact-block">
      <h2 className="subheading">Contact Us</h2>
      <ContactEnquiry />
    </div>
  );
}

function SlideFromSlideAnimation(props: any) {
  const [ref, inView] = useInView({
    triggerOnce: props.triggerOnce,
    rootMargin: props.rootMargin || "-10% 0px",
  });

  const isMobile = useSelector(getIsMobile);
  if (isMobile) {
    return <div className="feature">{props.children}</div>;
  }

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, x: props.initial || 0 }}
      animate={inView ? { opacity: 1, x: 0 } : {}}
      transition={{ duration: 1, ease: "easeOut" }}
      className="feature"
    >
      {props.children}
    </motion.div>
  );
}
